import { Button, Dialog, IconChevronDown } from '@madpaws/design-system';
import { useFormikContext } from 'formik';
import { useState } from 'react';

import { SITTER_REVIEWS_ANY_ID, SITTER_REVIEWS_OPTIONS } from '~/common/constants/search';
import { trackEvent } from '~/components/analytics/analytics';
import { UPDATE_MIN_REVIEWS_FILTER_SITTERS } from '~/components/analytics/constants';
import { DOM_CONTAINER_ID } from '~/components/constants';
import { getEventAllProperties } from '~/components/pages/SearchResults/tracking/getUpdatedSearchTrackingProperties';

import styles from './AdvancedFiltersMinReviewsDialog.module.css';
import { RadioGroupField } from '../../ui/RadioGroupField/RadioGroupField';

import type { ReactElement } from 'react';
import type { SearchFilters } from '~/common/types/search';

type Props = {
  handleAdvancedFiltersSubmit: () => void;
  isLoading: boolean;
};

const AdvancedFiltersMinReviewsDialog = ({
  handleAdvancedFiltersSubmit,
  isLoading,
}: Props): ReactElement => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { setFieldValue, initialValues, values } = useFormikContext<SearchFilters>();

  const handleFilterClear = (): void => {
    setFieldValue('advancedFilters.reviewsMin', SITTER_REVIEWS_ANY_ID);
  };

  const handleFilterReset = (): void => {
    const reviewsMin = initialValues.advancedFilters?.reviewsMin ?? SITTER_REVIEWS_ANY_ID;

    setFieldValue('advancedFilters.reviewsMin', reviewsMin);
  };

  const handleDialogToggle = (): void => {
    setIsDialogOpen(!isDialogOpen);
  };

  const handleDialogDismiss = (): void => {
    handleFilterReset();
    handleDialogToggle();
  };

  const handleFiltersDialogSearch = (): void => {
    const eventAllProperties = getEventAllProperties(values);

    trackEvent(UPDATE_MIN_REVIEWS_FILTER_SITTERS, eventAllProperties);

    handleDialogToggle();
    handleAdvancedFiltersSubmit();
  };

  const isChanged = values.advancedFilters?.reviewsMin !== SITTER_REVIEWS_ANY_ID;

  const getFilterButtonLabel = (): string => {
    const reviewsMin = values.advancedFilters?.reviewsMin;
    const reviewLabel = SITTER_REVIEWS_OPTIONS.find((option) => option.value === reviewsMin)?.label;

    if (!isChanged || !reviewLabel) {
      return 'Reviews';
    }

    return `${reviewLabel} Reviews`;
  };

  return (
    <div className={styles.root}>
      <div className={`${styles.reviewsMin} ${isChanged && styles.changed}`}>
        <Button
          iconTrailing={<IconChevronDown size="small2x" />}
          isDisabled={isLoading}
          isFullBleed
          label={getFilterButtonLabel()}
          onClick={handleDialogToggle}
          size="small1x"
          variant="tertiary"
        />
      </div>
      <Dialog
        cta={{
          size: 'large1x',
          label: 'Show Sitters',
          onClick: handleFiltersDialogSearch,
          isDisabled: isLoading,
        }}
        domContainerId={DOM_CONTAINER_ID}
        heading="Sitter reviews"
        isOpen={isDialogOpen}
        onRequestClose={handleDialogDismiss}
        secondaryAction={{
          label: 'Clear',
          onClick: handleFilterClear,
        }}
      >
        <RadioGroupField
          legend="Sitter reviews"
          name="advancedFilters.reviewsMin"
          options={SITTER_REVIEWS_OPTIONS}
          variant="tiled"
        />
      </Dialog>
    </div>
  );
};

export { AdvancedFiltersMinReviewsDialog };
